import React, { useContext, useEffect, useState } from "react";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./AppointmentsCalendar.scss";
import "./AppointmentsCalendar.css";
import AddAppointment from "./AddAppointment";
import axios from "axios";
import moment from "moment";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import CalendarDrawer from "./CalendarDrawer";
import { Error } from "../Messages/Messages";
import { isAuthenticated } from "../Auth/auth";
import { appointmentsToEvents } from "./appointmentEvents";
import { useAppointmentsLive } from "./appointmentApi";
import { getTimeZone } from "../../utils/shiftUtils";
import { SocketContext } from "../../context/SocketContext";
import PageToolbar from "../../design-system/PageToolbar";
import { useIsMobile } from "../../design-system/useMediaQuery";
import { calendarMessages } from "../../serviceProvider/components/shiftsPlanning/calendarMessages";

const localizer = momentLocalizer(moment);

const leaveConflictTitle = (event, t) =>
  `${event.title} · ${t("On leave")}: ${event.leaveConflicts.map(c => c.employeeName).join(", ")}`;

const AppointmentsCalendar = ({ serviceProvider, serviceProviderId, employeeId, clientId }) => {
  const { t, i18n } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [view, setView] = useState(isMobile ? Views.AGENDA : Views.MONTH);
  const [data, setData] = useState([]);
  const [drawerData, setDrawerData] = useState({});
  const [employeesArray, setEmployeesArray] = useState([]);
  const socket = useContext(SocketContext);

  const getData = async () => {
    await axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/appointments`,
        {
          serviceProvider: serviceProviderId,
          employee: employeeId,
          client: clientId,
          multipleAppointments: false,
          tz: getTimeZone() // leave days are compared in the user's timezone
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          setData(appointmentsToEvents(res.data?.data));
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        Error(err.response?.data?.errorMessage || err.message);
      });
  };

  const getEmployees = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${isAuthenticated()?.serviceProviderId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      if (res.status === 200 || res.status === 304) {
        const options = res.data.map(d => ({ label: d.username, value: d._id }));
        setEmployeesArray(options);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.message);
    }
  };

  // Refetch when someone else (e.g. a client confirming a slot) changes an appointment.
  useAppointmentsLive(socket, () => getData());

  useEffect(() => {
    getData();
    getEmployees();

    return () => {};
  }, []);

  const [open, setOpen] = useState(false);

  const showDrawer = e => {
    setOpen(true);
    setDrawerData(e);
  };

  const onClose = () => {
    setOpen(false);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = e => {
    setIsModalOpen(true);
    setDrawerData(e);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // The agenda (phone default) has no clickable events, so the title opens the appointment
  const AgendaEvent = ({ event }) => (
    <Button
      type="link"
      className="appointments-calendar__agenda-link"
      onClick={() => showModal(event)}
    >
      {event.title}
    </Button>
  );

  return (
    <div className="appointments-calendar">
      <PageToolbar
        actions={<AddAppointment employeesArray={employeesArray} updateParent={getData} />}
      />
      <div className="appointments-calendar__frame">
        <Calendar
          className={`appointments-calendar__calendar is-${view}`}
          localizer={localizer}
          view={view}
          onView={setView}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          events={data}
          startAccessor="start"
          endAccessor="end"
          timeslots={8}
          style={{ height: isMobile ? "calc(100dvh - 200px)" : 600, minHeight: 460 }}
          onSelectEvent={showModal}
          selectable
          resizable
          rtl={i18n.dir() === "rtl"}
          messages={calendarMessages(t, {
            event: t("Appointment"),
            noEventsInRange: t("No appointments in this period")
          })}
          components={{ agenda: { event: AgendaEvent } }}
          // Flag appointments whose employee has approved leave that day (e.g. sick leave
          // reported after booking); the edit dialog lists who.
          eventPropGetter={event =>
            event.leaveConflicts?.length
              ? { className: "is-leave-conflict", title: leaveConflictTitle(event, t) }
              : {}
          }
        />
      </div>

      <CalendarDrawer
        employeesArray={employeesArray}
        drawerData={drawerData}
        onClose={onClose}
        open={isModalOpen}
        updateParent={getData}
        handleCancel={handleCancel}
      />
    </div>
  );
};

export default AppointmentsCalendar;
